import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { API_SERVER_URL } from '@/constants/Config';
import type { IProductSelectionsResponse } from '@/common/interfaces/apparel.interface';
import type {
  IProductColorMoqResult,
  IProductColorsMoqRequest,
} from '@/modules/Cart/StoreCart/helpers/storeCartApiSync.utils';
import { parseProductColorsMoqApiResponse } from '@/modules/Cart/StoreCart/helpers/storeCartApiSync.utils';
import { normalizeProductSelectionsResponse } from '@/utilities/ApparelUtils';

export const productInfoQuery = createApi({
  reducerPath: 'productInfoQuery',
  baseQuery: axiosBaseQuery({
    baseUrl: API_SERVER_URL,
  }),
  endpoints: (build) => ({
    getProductInfo: build.query({
      query: ({ id }) => {
        const url = 'api/products/product';
        return {
          url,
          method: 'get',
          params: {
            id,
          },
        };
      },
    }),
    getProductSelections: build.query<IProductSelectionsResponse, { styleCode: string }>({
      queryFn: async ({ styleCode }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `api/v2/products/${encodeURIComponent(styleCode)}/selections`,
          method: 'get',
        });

        if (result.error) {
          return { error: result.error };
        }

        const selectionsData = normalizeProductSelectionsResponse(result.data ?? result);

        if (!selectionsData) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: 'Invalid product selections response',
            },
          };
        }

        return { data: selectionsData };
      },
    }),
    getProductColorsMoq: build.mutation<IProductColorMoqResult, IProductColorsMoqRequest>({
      queryFn: async (body, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: 'api/products/colors/moq',
          method: 'post',
          data: body,
        });

        if (result.error) {
          return { error: result.error };
        }

        const moqData = parseProductColorsMoqApiResponse(result);

        if (moqData) {
          return { data: moqData };
        }

        return {
          error: {
            status: 'CUSTOM_ERROR',
            data: 'Invalid product colors MOQ response',
          },
        };
      },
    }),
  }),
});

export const {
  useLazyGetProductInfoQuery,
  useLazyGetProductSelectionsQuery,
  useGetProductColorsMoqMutation,
} = productInfoQuery;
